import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import LayoutDashboard from '../../../components/layout/layout-dashboard';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Layers,
  Eye,
  EyeOff,
  Copy,
  Archive,
  Trash2,
  Star,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import ParcoursStatusBadge from '../../../components/admin/ParcoursStatusBadge';

type ParcoursStatus = 'draft' | 'published' | 'archived';

interface ParcoursRow {
  id: string;
  slug: string;
  company_name: string;
  sector: string | null;
  location: string | null;
  featured: boolean;
  status: ParcoursStatus;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  stepCount?: number;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function fmtDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(new Date(iso));
}

export default function ParcoursListAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [parcours, setParcours] = useState<ParcoursRow[]>([]);
  const [filtered, setFiltered] = useState<ParcoursRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ParcoursStatus | 'all'>('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    async function init() {
      if (!user) { navigate('/'); return; }
      const { data } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
      const role = data?.role ?? null;
      if (role !== 'admin' && role !== 'editor') { navigate('/'); return; }
      setUserRole(role);
      await load();
    }
    init();
  }, [user?.id]);

  async function load() {
    try {
      setLoading(true);

      const { data: rows, error } = await supabase
        .from('parcours')
        .select('id, slug, company_name, sector, location, featured, status, published_at, created_at, updated_at')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      const list: ParcoursRow[] = (rows ?? []).map((r: ParcoursRow) => ({ ...r, stepCount: undefined }));

      // Compter les étapes par parcours
      if (list.length > 0) {
        const { data: counts } = await supabase
          .from('parcours_steps')
          .select('parcours_id');

        if (counts) {
          const countMap: Record<string, number> = {};
          for (const row of counts) {
            countMap[row.parcours_id] = (countMap[row.parcours_id] ?? 0) + 1;
          }
          list.forEach((p) => { p.stepCount = countMap[p.id] ?? 0; });
        }
      }

      setParcours(list);
    } catch {
      toast.error('Impossible de charger les parcours.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let list = parcours;
    if (statusFilter !== 'all') list = list.filter((p) => p.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.company_name.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q) ||
          (p.sector ?? '').toLowerCase().includes(q) ||
          (p.location ?? '').toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [parcours, statusFilter, search]);

  async function handlePublishToggle(p: ParcoursRow) {
    const toastId = toast.loading(p.status === 'published' ? 'Dépublication...' : 'Publication...');
    try {
      const update =
        p.status === 'published'
          ? { status: 'draft' as ParcoursStatus }
          : { status: 'published' as ParcoursStatus, published_at: p.published_at ?? new Date().toISOString() };

      const { error } = await supabase.from('parcours').update(update).eq('id', p.id);
      if (error) throw error;
      toast.success(p.status === 'published' ? 'Parcours dépublié.' : 'Parcours publié.', { id: toastId });
      await load();
    } catch {
      toast.error('Impossible de modifier le statut.', { id: toastId });
    }
    setOpenMenu(null);
  }

  async function handleArchive(id: string) {
    if (!confirm('Archiver ce parcours ?')) return;
    const toastId = toast.loading('Archivage...');
    try {
      const { error } = await supabase.from('parcours').update({ status: 'archived' }).eq('id', id);
      if (error) throw error;
      toast.success('Parcours archivé.', { id: toastId });
      await load();
    } catch {
      toast.error('Impossible d\'archiver.', { id: toastId });
    }
    setOpenMenu(null);
  }

  async function handleDelete(id: string) {
    if (!confirm('Supprimer définitivement ce parcours et toutes ses étapes ? Cette action est irréversible.')) return;
    const toastId = toast.loading('Suppression...');
    try {
      const { error } = await supabase.from('parcours').delete().eq('id', id);
      if (error) throw error;
      toast.success('Parcours supprimé.', { id: toastId });
      await load();
    } catch {
      toast.error('Impossible de supprimer.', { id: toastId });
    }
    setOpenMenu(null);
  }

  async function handleDuplicate(p: ParcoursRow) {
    const toastId = toast.loading('Duplication...');
    try {
      // Construire un slug unique avec suffixe -copy
      const baseSlug = generateSlug(`${p.company_name} copy`);
      let slug = baseSlug;
      let attempt = 1;
      while (true) {
        const { data: existing } = await supabase
          .from('parcours')
          .select('id')
          .eq('slug', slug)
          .maybeSingle();
        if (!existing) break;
        attempt++;
        slug = `${baseSlug}-${attempt}`;
      }

      const { data: full } = await supabase
        .from('parcours')
        .select('*')
        .eq('id', p.id)
        .maybeSingle();

      if (!full) throw new Error('Parcours source introuvable');

      const { id: _id, created_at: _ca, updated_at: _ua, published_at: _pa, ...rest } = full;

      const { data: copy, error } = await supabase
        .from('parcours')
        .insert({ ...rest, company_name: `${p.company_name} (copie)`, slug, status: 'draft', published_at: null })
        .select('id')
        .single();

      if (error) throw error;

      // Dupliquer les étapes
      const { data: sourceSteps } = await supabase
        .from('parcours_steps')
        .select('step_id, title, description, icon, order_index, blocks, status')
        .eq('parcours_id', p.id)
        .order('order_index', { ascending: true });

      if (sourceSteps && sourceSteps.length > 0) {
        const stepsToInsert = sourceSteps.map((s) => ({ ...s, parcours_id: copy.id }));
        const { error: stepsError } = await supabase.from('parcours_steps').insert(stepsToInsert);
        if (stepsError) {
          toast.success('Parcours dupliqué, mais les étapes n\'ont pas pu être copiées.', { id: toastId });
          navigate(`/admin/parcours/${copy.id}/edit`);
          return;
        }
      }

      toast.success('Parcours et étapes dupliqués en brouillon.', { id: toastId });
      navigate(`/admin/parcours/${copy.id}/edit`);
    } catch {
      toast.error('Impossible de dupliquer.', { id: toastId });
    }
    setOpenMenu(null);
  }

  const counts = {
    all:       parcours.length,
    published: parcours.filter((p) => p.status === 'published').length,
    draft:     parcours.filter((p) => p.status === 'draft').length,
    archived:  parcours.filter((p) => p.status === 'archived').length,
  };

  const FILTER_LABELS: Record<string, string> = {
    all: 'Total', published: 'Publiés', draft: 'Brouillons', archived: 'Archivés',
  };

  return (
    <LayoutDashboard>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>

      <div className="mx-auto max-w-7xl px-6 py-10">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Parcours de découverte</h1>
            <p className="mt-1 text-sm text-slate-500">Gérez les parcours entreprise publiés sur recruteurs.io</p>
          </div>
          <Link
            to="/admin/parcours/new"
            className="inline-flex items-center gap-2 rounded-xl bg-pink-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-pink-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nouveau parcours
          </Link>
        </div>

        {/* Compteurs */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(['all', 'published', 'draft', 'archived'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-xl border p-4 text-left transition-all ${statusFilter === s ? 'border-slate-900 bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
            >
              <p className="text-2xl font-bold text-slate-900">{counts[s]}</p>
              <p className="mt-0.5 text-xs font-medium text-slate-500">{FILTER_LABELS[s]}</p>
            </button>
          ))}
        </div>

        {/* Recherche */}
        <div className="mt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par entreprise, slug, secteur ou ville..."
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-slate-900"
            />
          </div>
        </div>

        {/* Tableau */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-slate-500 text-sm">Aucun parcours trouvé.</p>
              <Link
                to="/admin/parcours/new"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700"
              >
                <Plus className="h-4 w-4" /> Créer le premier parcours
              </Link>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Entreprise</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Statut</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 sm:table-cell">Étapes</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 md:table-cell">En avant</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 lg:table-cell">Modifié le</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                    {/* Entreprise */}
                    <td className="px-5 py-4">
                      <p className="font-medium text-slate-900 text-sm">{p.company_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{p.slug}</p>
                    </td>

                    {/* Statut */}
                    <td className="px-5 py-4">
                      <ParcoursStatusBadge status={p.status} />
                    </td>

                    {/* Étapes */}
                    <td className="hidden px-5 py-4 text-sm text-slate-600 sm:table-cell">
                      {p.stepCount !== undefined ? p.stepCount : '—'}
                    </td>

                    {/* En avant */}
                    <td className="hidden px-5 py-4 md:table-cell">
                      {p.featured
                        ? <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        : <span className="text-slate-300 text-sm">—</span>
                      }
                    </td>

                    {/* Modifié le */}
                    <td className="hidden px-5 py-4 text-sm text-slate-500 lg:table-cell">
                      {fmtDate(p.updated_at)}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-4">
                      <div className="relative flex justify-end">
                        <button
                          onClick={(e) => {
                            if (openMenu === p.id) { setOpenMenu(null); setMenuPos(null); return; }
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setMenuPos({ top: rect.bottom + window.scrollY + 4, right: window.innerWidth - rect.right });
                            setOpenMenu(p.id);
                          }}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>

                        {openMenu === p.id && menuPos && (
                          <div
                            className="fixed z-50 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
                            style={{ top: menuPos.top, right: menuPos.right }}
                          >
                            <Link
                              to={`/admin/parcours/${p.id}/edit`}
                              className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                              onClick={() => setOpenMenu(null)}
                            >
                              <Pencil className="h-4 w-4 text-slate-400" /> Modifier
                            </Link>
                            <Link
                              to={`/admin/parcours/${p.id}/steps`}
                              className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                              onClick={() => setOpenMenu(null)}
                            >
                              <Layers className="h-4 w-4 text-slate-400" /> Gérer les étapes
                            </Link>
                            <Link
                              to={`/admin/parcours/${p.id}/preview`}
                              className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                              onClick={() => setOpenMenu(null)}
                            >
                              <Eye className="h-4 w-4 text-slate-400" /> Prévisualiser
                            </Link>

                            <div className="border-t border-slate-100" />

                            <button
                              onClick={() => handlePublishToggle(p)}
                              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              {p.status === 'published'
                                ? <><EyeOff className="h-4 w-4 text-slate-400" /> Dépublier</>
                                : <><Eye className="h-4 w-4 text-slate-400" /> Publier</>
                              }
                            </button>
                            <button
                              onClick={() => handleDuplicate(p)}
                              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <Copy className="h-4 w-4 text-slate-400" /> Dupliquer
                            </button>
                            {p.status !== 'archived' && (
                              <button
                                onClick={() => handleArchive(p.id)}
                                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                <Archive className="h-4 w-4 text-slate-400" /> Archiver
                              </button>
                            )}

                            {userRole === 'admin' && (
                              <button
                                onClick={() => handleDelete(p.id)}
                                className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" /> Supprimer
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Overlay fermeture menu */}
      {openMenu && (
        <div className="fixed inset-0 z-40" onClick={() => { setOpenMenu(null); setMenuPos(null); }} />
      )}
    </LayoutDashboard>
  );
}
